import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import type { ISBEFElementGroup } from '../../hooks/use-report-elements';
import { SBEF_BAR_LIST_ROW_PITCH_PERCENT } from './report-product-groups.constants';
import type { SbefSegment } from './report-product-groups.model';

/**
 * Pair each baseline SBEF group with its CO₂e/m² in the proposal so the
 * proposal bar can be split per group ({@link SbefSegment}). Order follows the
 * baseline group order (SBEF order, same as the element list).
 *
 * Groups present only in the proposal (no baseline counterpart) are dropped:
 * with no baseline magnitude there is no width to allocate and the height ratio
 * would be undefined. Baseline groups already carry positive CO₂e (the source
 * hook filters zero rows), so every returned segment has a usable width.
 *
 * @param baselineGroups SBEF groups for the baseline scenario.
 * @param proposalGroups SBEF groups for the proposal scenario.
 */
export const buildSbefSegments = (
  baselineGroups: ISBEFElementGroup[],
  proposalGroups: ISBEFElementGroup[],
): SbefSegment[] => {
  const proposalCo2ByKey = new Map(
    proposalGroups.map((group) => [
      group.groupKey,
      getConversionFactorValue(group.sums, 'co2e'),
    ]),
  );

  return baselineGroups.flatMap((group) => {
    const baselineCo2e = getConversionFactorValue(group.sums, 'co2e');
    if (baselineCo2e <= 0) return [];
    return [
      {
        groupKey: group.groupKey,
        label: group.label,
        baselineCo2e,
        proposalCo2e: proposalCo2ByKey.get(group.groupKey) ?? 0,
      },
    ];
  });
};

export type PlacedSbefHorizontalBar = SbefSegment & {
  /** Bar length (color + grey remainder) as % of the row's available track width. */
  totalWidthPct: number;
  /** Colored (proposal) portion as % of the row's available track width. */
  fillWidthPct: number;
  /** Row center (% from plot top) — bottom row sits above plot foot. */
  rowTopPct: number;
};

/**
 * Lay out one horizontal bar per SBEF group: bar length is proportional to
 * the group's baseline CO₂e against the largest baseline group in the list,
 * and the colored portion is the proposal's CO₂e on that same scale — so a
 * shorter colored fill within a bar reads as a bigger reduction. Rows sit
 * {@link SBEF_BAR_LIST_ROW_PITCH_PERCENT} apart (bar height + a donut-slice-gap-
 * sized gap) and are centered as a block within the plot height.
 *
 * @param segments Ordered SBEF segments for one proposal.
 */
export const placeSbefHorizontalBars = (
  segments: SbefSegment[],
): PlacedSbefHorizontalBar[] => {
  const maxBaselineCo2e = Math.max(0, ...segments.map((s) => s.baselineCo2e));
  const totalSpanPct = (segments.length - 1) * SBEF_BAR_LIST_ROW_PITCH_PERCENT;
  const firstRowTopPct = (100 - totalSpanPct) / 2;

  const placed = segments.map((segment, index) => {
    const totalWidthPct =
      maxBaselineCo2e > 0 ? (segment.baselineCo2e / maxBaselineCo2e) * 100 : 0;
    const fillWidthPct =
      maxBaselineCo2e > 0 ? (segment.proposalCo2e / maxBaselineCo2e) * 100 : 0;
    const rowTopPct = firstRowTopPct + index * SBEF_BAR_LIST_ROW_PITCH_PERCENT;

    return { ...segment, totalWidthPct, fillWidthPct, rowTopPct };
  });

  // If any proposal bar exceeds the baseline max width, scale all widths down
  // uniformly so the tallest bar fits within the available track (100%).
  const maxFillWidth = Math.max(0, ...placed.map((s) => s.fillWidthPct));
  if (maxFillWidth > 100) {
    const scale = 100 / maxFillWidth;
    return placed.map((s) => ({
      ...s,
      totalWidthPct: s.totalWidthPct * scale,
      fillWidthPct: s.fillWidthPct * scale,
    }));
  }
  return placed;
};
